/** Enterprise org seat pool: an org admin's own portal for adding/removing
 * contractor seats by email, modelled on Bluebeam's Org Admin (Users tab +
 * Modify Plan/Deactivate) -- see ROADMAP.md's "Org seat pools" entry.
 *
 * Only rendered for config.org_role === 'admin' (see App.tsx's nav gating);
 * a plain member never reaches this view.
 */

import { useCallback, useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { OrgInfo, OrgPricing, OrgSeat, SeatPurchaseRequest } from '../lib/types';
import { Callout, InboxIcon, Spinner, formatDateTime } from './primitives';

function formatCents(cents: number | null): string {
  if (cents === null) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function OrgPortalView() {
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [seats, setSeats] = useState<OrgSeat[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const load = useCallback(async (currentSearch: string) => {
    setBusy(true);
    setError(null);
    try {
      const [orgInfo, seatPage] = await Promise.all([api.getMyOrg(), api.listOrgSeats(currentSearch)]);
      setOrg(orgInfo);
      setSeats(seatPage.seats);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load the seat pool.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    await load(search);
  }

  async function runRowAction(accountId: string, message: string, action: () => Promise<unknown>) {
    setRowBusy(accountId);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(message);
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That action failed.');
    } finally {
      setRowBusy(null);
    }
  }

  const seatsFull = org ? org.seats_used >= org.seat_limit : false;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>{org?.name ?? 'Seat pool'}</h2>
          <div className="masthead-spacer" />
          {org && (
            <span className="hint">
              {org.seats_used} of {org.seat_limit} seats used
            </span>
          )}
          <button type="button" className="btn btn-sm btn-accent" onClick={() => setPurchaseOpen(true)}>
            Purchase more seats
          </button>
          <button type="button" className="btn btn-sm" onClick={() => void refresh()} disabled={busy}>
            Refresh
          </button>
        </div>

        {error && (
          <div className="card-body tight">
            <Callout variant="error">{error}</Callout>
          </div>
        )}
        {notice && (
          <div className="card-body tight">
            <Callout variant="info">{notice}</Callout>
          </div>
        )}
        {seatsFull && (
          <div className="card-body tight">
            <Callout variant="upsell">
              This seat pool is full. Remove a seat before adding another, or contact ClearIFC to
              raise the limit.
            </Callout>
          </div>
        )}

        <div className="card-body tight row">
          <input
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void load(search);
            }}
            style={{ flex: 1, minWidth: '14rem' }}
            aria-label="Search seats"
          />
          <button type="button" className="btn btn-sm" onClick={() => void load(search)}>
            Search
          </button>
          <button
            type="button"
            className="btn btn-sm btn-accent"
            onClick={() => setAddOpen(true)}
            disabled={seatsFull}
          >
            Add seat
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setBulkOpen(true)} disabled={seatsFull}>
            Bulk add
          </button>
        </div>

        {busy && (
          <div className="card-body tight row">
            <Spinner /> <span className="hint-strong">Loading…</span>
          </div>
        )}

        {!busy && seats.length === 0 ? (
          <div className="empty">
            <InboxIcon />
            <p>No seats yet.</p>
            <p className="hint">Add a contractor's email to give them access under this plan.</p>
          </div>
        ) : (
          !busy && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th>Joined</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {seats.map((seat) => (
                    <tr key={seat.id}>
                      <td>{seat.name}</td>
                      <td className="cell-mono">{seat.email}</td>
                      <td>{seat.org_role}</td>
                      <td>
                        {seat.disabled
                          ? 'Deactivated'
                          : seat.has_password
                            ? 'Active'
                            : 'Invited (pending)'}
                      </td>
                      <td className="cell-mono">{formatDateTime(seat.last_login)}</td>
                      <td className="cell-mono">{formatDateTime(seat.org_joined_at)}</td>
                      <td>
                        <div className="row">
                          {!seat.has_password && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              disabled={rowBusy === seat.id}
                              onClick={() =>
                                void runRowAction(
                                  seat.id,
                                  `Invite resent to ${seat.email}.`,
                                  () => api.resendOrgInvite(seat.id),
                                )
                              }
                            >
                              Resend invite
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={rowBusy === seat.id}
                            onClick={() =>
                              void runRowAction(
                                seat.id,
                                seat.disabled
                                  ? `${seat.email} reactivated.`
                                  : `${seat.email} deactivated.`,
                                () =>
                                  seat.disabled
                                    ? api.reactivateOrgSeat(seat.id)
                                    : api.deactivateOrgSeat(seat.id),
                              )
                            }
                          >
                            {seat.disabled ? 'Reactivate' : 'Deactivate'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={rowBusy === seat.id}
                            onClick={() =>
                              void runRowAction(
                                seat.id,
                                seat.org_role === 'admin'
                                  ? `${seat.email} is now a member.`
                                  : `${seat.email} is now an admin.`,
                                () =>
                                  api.setOrgSeatRole(
                                    seat.id,
                                    seat.org_role === 'admin' ? 'member' : 'admin',
                                  ),
                              )
                            }
                          >
                            {seat.org_role === 'admin' ? 'Make member' : 'Make admin'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={rowBusy === seat.id}
                            onClick={() =>
                              void runRowAction(
                                seat.id,
                                `${seat.email} removed from the seat pool.`,
                                () => api.removeOrgSeat(seat.id),
                              )
                            }
                          >
                            Remove seat
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {addOpen && (
        <AddSeatDialog
          onClose={() => setAddOpen(false)}
          onAdded={async (message) => {
            setAddOpen(false);
            setNotice(message);
            await refresh();
          }}
        />
      )}
      {bulkOpen && (
        <BulkAddDialog
          onClose={() => setBulkOpen(false)}
          onAdded={async (message) => {
            setBulkOpen(false);
            setNotice(message);
            await refresh();
          }}
        />
      )}
      {purchaseOpen && (
        <PurchaseSeatsDialog
          onClose={() => setPurchaseOpen(false)}
          onSubmitted={(message) => {
            setPurchaseOpen(false);
            setNotice(message);
          }}
        />
      )}
    </div>
  );
}

function AddSeatDialog({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (message: string) => void | Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const seat = await api.addOrgSeat({ email, name: name || undefined });
      await onAdded(`${seat.email} was added to the seat pool.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add that seat.');
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Add a seat">
      <div className="card">
        <div className="card-head">
          <h2>Add a seat</h2>
        </div>
        <div className="card-body stack">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoFocus
            aria-label="Email address"
          />
          <input
            type="text"
            placeholder="Name (optional)"
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-label="Name"
          />
          {error && <Callout variant="error">{error}</Callout>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => void submit()}
              disabled={busy || !email.includes('@')}
            >
              {busy ? 'Adding…' : 'Add seat'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mirrors the server-side proration in _prorated_amount_cents
 * (routes_org.py) for an instant preview as the admin changes seats/date --
 * the actual request always recomputes this authoritatively server-side,
 * this is display-only. */
function estimateCents(
  pricing: OrgPricing,
  seats: number,
  startDateIso: string,
): { amount: number | null; note: string } {
  if (pricing.price_per_seat_cents === null) {
    return { amount: null, note: 'No per-seat rate is configured for this org yet.' };
  }
  const fullPrice = pricing.price_per_seat_cents * seats;
  if (!pricing.term_start_date || !pricing.term_end_date) {
    return { amount: fullPrice, note: "No contract term is on file for this org, so this is the full rate, not pro-rated." };
  }
  const start = new Date(startDateIso);
  const termStart = new Date(pricing.term_start_date);
  const termEnd = new Date(pricing.term_end_date);
  if (start >= termEnd) {
    return { amount: fullPrice, note: 'Starts on or after the current term ends -- full rate for a new term.' };
  }
  const effectiveStart = start < termStart ? termStart : start;
  const msPerDay = 24 * 60 * 60 * 1000;
  const remainingDays = Math.round((termEnd.getTime() - effectiveStart.getTime()) / msPerDay);
  const totalDays = Math.max(1, Math.round((termEnd.getTime() - termStart.getTime()) / msPerDay));
  return {
    amount: Math.round((fullPrice * remainingDays) / totalDays),
    note: `Pro-rated: ${remainingDays} of ${totalDays} days remaining in the current term.`,
  };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function PurchaseSeatsDialog({
  onClose,
  onSubmitted,
}: {
  onClose: () => void;
  onSubmitted: (message: string) => void;
}) {
  const [pricing, setPricing] = useState<OrgPricing | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [seats, setSeats] = useState(5);
  const [paymentChoice, setPaymentChoice] = useState<'pay_now' | 'invoice'>('pay_now');
  const [startDate, setStartDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pastRequests, setPastRequests] = useState<SeatPurchaseRequest[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const [p, history] = await Promise.all([api.getOrgPricing(), api.listPurchaseRequests()]);
        setPricing(p);
        if (p.term_end_date) setStartDate(p.term_end_date);
        setPastRequests(history.requests);
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Could not load pricing.');
      } finally {
        setLoadingPricing(false);
      }
    })();
  }, []);

  const effectiveStartDate = paymentChoice === 'pay_now' ? todayIso() : startDate;
  const preview = pricing ? estimateCents(pricing, seats, effectiveStartDate) : null;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const request = await api.requestMoreSeats({
        seats,
        payment_choice: paymentChoice,
        start_date: paymentChoice === 'invoice' ? startDate : undefined,
      });
      if (request.checkout_url) {
        // Hand off to Stripe immediately -- the seat limit only actually
        // rises once Stripe confirms payment via webhook, not from this
        // redirect itself.
        window.location.href = request.checkout_url;
        return;
      }
      const amount = formatCents(request.prorated_amount_cents);
      onSubmitted(
        request.prorated_amount_cents !== null
          ? `Request submitted: ${seats} seat(s), estimated ${amount}. ClearIFC will follow up on ${
              paymentChoice === 'pay_now' ? 'payment' : 'invoicing'
            }.`
          : `Request submitted: ${seats} seat(s). No rate is configured yet -- ClearIFC will follow up directly with pricing.`,
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not submit that request.');
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Purchase more seats">
      <div className="card" style={{ width: 'min(480px, calc(100vw - 2rem))' }}>
        <div className="card-head">
          <h2>Purchase more seats</h2>
        </div>
        <div className="card-body stack">
          {loadingPricing ? (
            <div className="row">
              <Spinner /> <span className="hint-strong">Loading pricing…</span>
            </div>
          ) : (
            <>
              {pricing?.price_per_seat_cents === null && (
                <Callout variant="upsell">
                  This org has no per-seat rate configured yet. You can still submit a request --
                  ClearIFC will follow up with pricing directly.
                </Callout>
              )}

              <label className="field">
                <span>Additional seats</span>
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={seats}
                  onChange={(event) => setSeats(Math.max(1, Number(event.target.value) || 1))}
                />
              </label>

              <div className="field">
                <span>Payment</span>
                <div className="stack" style={{ gap: '0.4rem' }}>
                  <label className="row" style={{ gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="payment_choice"
                      checked={paymentChoice === 'pay_now'}
                      onChange={() => setPaymentChoice('pay_now')}
                    />
                    <span>Pay now (starting today)</span>
                  </label>
                  <label className="row" style={{ gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="payment_choice"
                      checked={paymentChoice === 'invoice'}
                      onChange={() => setPaymentChoice('invoice')}
                    />
                    <span>Invoice me, starting on a date I choose</span>
                  </label>
                </div>
              </div>

              {paymentChoice === 'invoice' && (
                <label className="field">
                  <span>Start date</span>
                  <input
                    type="date"
                    min={todayIso()}
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </label>
              )}

              {preview && (
                <Callout variant={preview.amount === null ? 'upsell' : 'info'}>
                  <strong>Estimated: {formatCents(preview.amount)}</strong>
                  <div style={{ marginTop: '0.2rem' }} className="hint">
                    {preview.note}
                  </div>
                </Callout>
              )}

              {pastRequests.length > 0 && (
                <div>
                  <div className="field-label">Past requests</div>
                  <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                    {pastRequests.slice(0, 5).map((r) => (
                      <li key={r.id} className="hint">
                        {r.seats_requested} seat(s), {formatCents(r.prorated_amount_cents)} —{' '}
                        <strong>{r.status}</strong> ({formatDateTime(r.created_at)})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {error && <Callout variant="error">{error}</Callout>}

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => void submit()}
              disabled={busy || loadingPricing}
            >
              {busy ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkAddDialog({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (message: string) => void | Promise<void>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const emails = Array.from(
      new Set(
        text
          .split(/[\s,;]+/)
          .map((entry) => entry.trim().toLowerCase())
          .filter((entry) => entry.includes('@')),
      ),
    );
    if (emails.length === 0) {
      setError('Paste at least one email address.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.addOrgSeatsBulk(emails);
      const summary =
        result.failed.length === 0
          ? `Added ${result.added.length} seat(s).`
          : `Added ${result.added.length} seat(s); ${result.failed.length} failed (${result.failed
              .map((f) => f.email)
              .join(', ')}).`;
      await onAdded(summary);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add those seats.');
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Bulk add seats">
      <div className="card" style={{ width: 'min(520px, calc(100vw - 2rem))' }}>
        <div className="card-head">
          <h2>Bulk add seats</h2>
        </div>
        <div className="card-body stack">
          <p className="hint">Paste emails separated by commas, spaces or new lines.</p>
          <textarea
            rows={8}
            placeholder={'alice@contractor.com\nbob@contractor.com'}
            value={text}
            onChange={(event) => setText(event.target.value)}
            autoFocus
            aria-label="Email addresses"
          />
          {error && <Callout variant="error">{error}</Callout>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-accent" onClick={() => void submit()} disabled={busy}>
              {busy ? 'Adding…' : 'Add seats'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
